import { io, Socket } from 'socket.io-client'
import {
  ChatSocketEvent,
  ChatStatusUpdatedEventPayload,
  NewChatCreatedEventPayload,
  ChatReadEventPayload,
  NewMessageEventPayload,
  FloristAssignedPayload,
  FloristConfirmedPayload,
  FloristDeclinedPayload,
  FloristBouquetReadyPayload,
  OrderRealtimePayload,
} from '@tenant/shared'
import { logger } from '../utils/logger'

class AdminSocketService {
  private socket: Socket | null = null
  private isConnected = false
  private listeners: Map<ChatSocketEvent | string, Array<(...args: any[]) => void>> = new Map()
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private currentToken: string | null = null
  private reconnectTimeout: NodeJS.Timeout | null = null

  private applyAuthToken(token: string) {
    if (!this.socket) return
    ;(this.socket as any).auth = { token }
    ;(this.socket.io.opts as any).auth = { token }
  }

  connect(token: string) {
    // Yenidən qoşulma üçün tokeni saxlayırıq
    this.currentToken = token

    // FIX: Yaddaş sızmalarının qarşısını almaq üçün köhnə qoşulmaların düzgün təmizlənməsi
    if (this.socket) {
      // Artıq qoşulma varsa, tokeni yeniləyirik və yenidən istifadə edirik
      this.applyAuthToken(token)
      if (!this.socket.connected && !this.socket.active) {
        this.socket.connect()
      }
      return this.socket
    }

    const rawApiBase = ((import.meta as any)?.env?.VITE_API_URL ?? 'http://localhost:3001/api') as string
    const API_BASE_URL = rawApiBase.replace(/\/$/, '').replace(/\/api$/, '')

    logger.socket('Connecting to', API_BASE_URL)

    this.socket = io(API_BASE_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      upgrade: true,
      rememberUpgrade: true,
      // FIX: Stabil bağlantı üçün parametrlər
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    })

    this.socket.on('connect', () => {
      logger.socket('Connected', this.socket?.id)
      this.isConnected = true
      this.reconnectAttempts = 0

      // Yenidən qoşulma taymerini təmizləyirik
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout)
        this.reconnectTimeout = null
      }

      // join-room-dan əvvəl qoşulma təsdiqini gözləyirik
      setTimeout(() => {
        if (this.socket?.connected) {
          this.socket.emit('join-room', 'admin-global')
        }
      }, 100)
    })

    this.socket.on('disconnect', (reason) => {
      logger.socket('Disconnected', reason)
      this.isConnected = false
    })

    this.socket.on('connect_error', (error) => {
      logger.error('Socket.IO connection error', error)
      this.reconnectAttempts++

      if ((error as any)?.message === 'Invalid token' || (error as any)?.data?.message === 'Invalid token') {
        logger.error('Socket authentication failed due to invalid token')
        this.disconnect()
        return
      }

      // FIX: Limitə çatdıqdan sonra yenidən qoşulmaların tam dayandırılması
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        logger.error('Max reconnection attempts reached, stopping reconnection')
        this.disconnect()

        // İxtiyari: 30 saniyədən sonra avtomatik yenidən qoşulma
        this.reconnectTimeout = setTimeout(() => {
          logger.socket('Attempting to reconnect after cooldown period')
          this.reconnectAttempts = 0
          if (this.currentToken) {
            this.reconnect()
          }
        }, 30000)
      }
    })

    this.setupEventListeners()
    return this.socket
  }

  reconnect() {
    if (this.currentToken) {
      this.disconnect()
      this.connect(this.currentToken)
    }
  }

  updateToken(token: string) {
    this.currentToken = token
    if (this.socket) {
      this.applyAuthToken(token)
      if (!this.socket.connected && !this.socket.active) {
        this.socket.connect()
      }
    } else {
      this.connect(token)
    }
  }

  private setupEventListeners() {
    if (!this.socket) return

    // Yenilərini qeydiyyata almadan əvvəl əvvəlki idarəediciləri təmizləyirik
    // Chat events
    this.socket.off(ChatSocketEvent.NEW_MESSAGE)
    this.socket.off(ChatSocketEvent.CHAT_STATUS_UPDATED)
    this.socket.off(ChatSocketEvent.NEW_CHAT_CREATED)
    this.socket.off(ChatSocketEvent.CHAT_READ)

    // Order events
    this.socket.off('order:updated')
    this.socket.off('order:florist_assigned')
    this.socket.off('order:bouquet_ready')
    this.socket.off('order:courier_assigned')
    this.socket.off('order:in_delivery')
    this.socket.off('order:delivered')
    this.socket.off('order:cancelled')

    // Florist events
    this.socket.off('florist:assigned')
    this.socket.off('florist:confirmed')
    this.socket.off('florist:declined')
    this.socket.off('florist:bouquet_ready')

    // === CHAT EVENTS ===
    this.socket.on(ChatSocketEvent.NEW_MESSAGE, (data: NewMessageEventPayload) => {
      logger.socket('New message received', data.chatId)
      this.emit(ChatSocketEvent.NEW_MESSAGE, data)
    })

    this.socket.on(ChatSocketEvent.CHAT_STATUS_UPDATED, (data: ChatStatusUpdatedEventPayload) => {
      logger.socket('Chat status updated', data.chatId)
      this.emit(ChatSocketEvent.CHAT_STATUS_UPDATED, data)
    })

    this.socket.on(ChatSocketEvent.NEW_CHAT_CREATED, (data: NewChatCreatedEventPayload) => {
      logger.socket('New chat created', data.chat.id)
      this.emit(ChatSocketEvent.NEW_CHAT_CREATED, data)
    })

    this.socket.on(ChatSocketEvent.CHAT_READ, (data: ChatReadEventPayload) => {
      logger.socket('Chat marked as read', data.chatId)
      this.emit(ChatSocketEvent.CHAT_READ, data)
    })

    // === ORDER EVENTS ===
    this.socket.on('order:updated', (data: OrderRealtimePayload) => {
      logger.socket('Order updated', { orderId: data.orderId, status: data.status })
      this.emit('order:updated', data)
    })

    this.socket.on('order:florist_assigned', (data: OrderRealtimePayload) => {
      logger.socket('Florist assigned to order', data.orderId)
      this.emit('order:florist_assigned', data)
    })

    this.socket.on('order:bouquet_ready', (data: OrderRealtimePayload) => {
      logger.socket('Bouquet ready for order', data.orderId)
      this.emit('order:bouquet_ready', data)
    })

    this.socket.on('order:courier_assigned', (data: OrderRealtimePayload) => {
      logger.socket('Courier assigned to order', data.orderId)
      this.emit('order:courier_assigned', data)
    })

    this.socket.on('order:in_delivery', (data: OrderRealtimePayload) => {
      logger.socket('Order in delivery', data.orderId)
      this.emit('order:in_delivery', data)
    })

    this.socket.on('order:delivered', (data: OrderRealtimePayload) => {
      logger.socket('Order delivered', data.orderId)
      this.emit('order:delivered', data)
    })

    this.socket.on('order:cancelled', (data: OrderRealtimePayload) => {
      logger.socket('Order cancelled', data.orderId)
      this.emit('order:cancelled', data)
    })

    // === FLORIST EVENTS ===
    this.socket.on('florist:assigned', (data: FloristAssignedPayload) => {
      logger.socket('Florist assigned event', { orderId: data.orderId, floristId: data.floristId })
      this.emit('florist:assigned', data)
    })

    this.socket.on('florist:confirmed', (data: FloristConfirmedPayload) => {
      logger.socket('Florist confirmed order', data.orderId)
      this.emit('florist:confirmed', data)
    })

    this.socket.on('florist:declined', (data: FloristDeclinedPayload) => {
      logger.socket('Florist declined order', { orderId: data.orderId, reason: data.reason })
      this.emit('florist:declined', data)
    })

    this.socket.on('florist:bouquet_ready', (data: FloristBouquetReadyPayload) => {
      logger.socket('Florist marked bouquet ready', data.orderId)
      this.emit('florist:bouquet_ready', data)
    })
  }

  joinChatRoom(chatId: string) {
    if (this.socket?.connected) {
      this.socket.emit('join-room', `chat-${chatId}`)
      logger.socket('Joined chat room', chatId)
    } else {
      logger.warn(`Cannot join chat room ${chatId}: socket not connected`)
    }
  }

  leaveChatRoom(chatId: string) {
    if (this.socket?.connected) {
      this.socket.emit('leave-room', `chat-${chatId}`)
      logger.socket('Left chat room', chatId)
    }
  }

  on(event: ChatSocketEvent | string, callback: (...args: any[]) => void) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, [])
    }
    this.listeners.get(event)!.push(callback)
  }

  off(event: ChatSocketEvent | string, callback: (...args: any[]) => void) {
    const eventListeners = this.listeners.get(event)
    if (eventListeners) {
      const index = eventListeners.indexOf(callback)
      if (index > -1) {
        eventListeners.splice(index, 1)
      }
    }
  }

  private emit(event: ChatSocketEvent | string, ...args: any[]) {
    const eventListeners = this.listeners.get(event)
    if (eventListeners) {
      eventListeners.forEach((callback) => callback(...args))
    }
  }

  disconnect() {
    // FIX: Yenidən qoşulma taymerini təmizləyirik
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }

    if (this.socket) {
      // Disconnect-dən əvvəl bütün event listener-ləri silirik
      this.socket.removeAllListeners()
      this.socket.disconnect()
      this.socket = null
      this.isConnected = false
      this.listeners.clear()
      logger.socket('Disconnected manually')
    }
  }

  get connected() {
    return this.isConnected && !!this.socket?.connected
  }

  get id() {
    return this.socket?.id
  }

  get socketInstance(): Socket | null {
    return this.socket
  }

  getSocket(): Socket | null {
    return this.socket
  }
}

export const adminSocketService = new AdminSocketService()
export default adminSocketService




